import React, { useState, useEffect } from 'react'
import { api } from '../services/authService'
import {
  PlusIcon,
  FunnelIcon,
  ArrowPathIcon,
  BuildingOffice2Icon,
  UserGroupIcon,
  CurrencyDollarIcon,
  ClockIcon,
  XMarkIcon,
  ArrowsRightLeftIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value) || 0)
}

const MarketingPipelinePage = () => {
  const [stages, setStages] = useState([])
  const [loading, setLoading] = useState(true)
  const [filterAssignedTo, setFilterAssignedTo] = useState('')
  const [filterSector, setFilterSector] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [showNewModal, setShowNewModal] = useState(false)
  const [leads, setLeads] = useState([])
  const [movingOpportunity, setMovingOpportunity] = useState(null)
  const [formData, setFormData] = useState({
    lead_id: '',
    title: '',
    monthly_value: '',
    total_vehicles: '',
    contract_duration: '',
    probability: '',
    expected_close_date: '',
    notes: ''
  })
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    loadPipeline()
  }, [filterAssignedTo, filterSector])

  const loadPipeline = async () => {
    setLoading(true)
    try {
      const params = {}
      if (filterAssignedTo) params.assigned_to = filterAssignedTo
      if (filterSector) params.sector = filterSector

      const response = await api.get('/api/marketing/pipeline', { params })
      setStages(response.data.data || response.data || [])
    } catch (error) {
      console.error('Erro ao carregar pipeline:', error)
      toast.error('Erro ao carregar pipeline de vendas')
    } finally {
      setLoading(false)
    }
  }

  const loadLeads = async () => {
    try {
      const response = await api.get('/api/marketing/leads', { params: { limit: 100 } })
      setLeads(response.data.data || response.data || [])
    } catch (error) {
      console.error('Erro ao carregar leads:', error)
      toast.error('Erro ao carregar leads')
    }
  }

  const handleOpenNewModal = () => {
    setFormData({
      lead_id: '',
      title: '',
      monthly_value: '',
      total_vehicles: '',
      contract_duration: '',
      probability: '',
      expected_close_date: '',
      notes: ''
    })
    loadLeads()
    setShowNewModal(true)
  }

  const handleCloseNewModal = () => {
    setShowNewModal(false)
  }

  const handleFormChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmitOpportunity = async (e) => {
    e.preventDefault()

    if (!formData.title.trim()) {
      toast.error('Informe o titulo da oportunidade')
      return
    }
    if (!formData.monthly_value) {
      toast.error('Informe o valor mensal')
      return
    }

    setSubmitting(true)
    try {
      const payload = {
        ...formData,
        monthly_value: parseFloat(formData.monthly_value) || 0,
        total_vehicles: parseInt(formData.total_vehicles) || 0,
        contract_duration: parseInt(formData.contract_duration) || 0,
        probability: parseInt(formData.probability) || 0,
        lead_id: formData.lead_id ? parseInt(formData.lead_id) : null
      }

      await api.post('/api/marketing/pipeline/opportunities', payload)
      toast.success('Oportunidade criada com sucesso!')
      setShowNewModal(false)
      loadPipeline()
    } catch (error) {
      console.error('Erro ao criar oportunidade:', error)
      const msg = error.response?.data?.error || 'Erro ao criar oportunidade'
      toast.error(msg)
    } finally {
      setSubmitting(false)
    }
  }

  const handleMoveOpportunity = async (opportunityId, newStageId) => {
    try {
      await api.put(`/api/marketing/pipeline/opportunities/${opportunityId}/move`, {
        stage_id: newStageId
      })
      toast.success('Oportunidade movida com sucesso!')
      setMovingOpportunity(null)
      loadPipeline()
    } catch (error) {
      console.error('Erro ao mover oportunidade:', error)
      const msg = error.response?.data?.error || 'Erro ao mover oportunidade'
      toast.error(msg)
    }
  }

  const clearFilters = () => {
    setFilterAssignedTo('')
    setFilterSector('')
  }

  const hasActiveFilters = filterAssignedTo || filterSector

  const totalPipelineValue = stages.reduce((sum, stage) => sum + (Number(stage.total_value) || 0), 0)
  const totalOpportunities = stages.reduce((sum, stage) => sum + (Number(stage.count) || 0), 0)

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-3">
          <ArrowPathIcon className="h-8 w-8 text-blue-500 animate-spin" />
          <p className="text-gray-500 dark:text-gray-400">Carregando pipeline...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 md:p-6 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Pipeline de Vendas
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Gerencie suas oportunidades comerciais
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                hasActiveFilters
                  ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-600 dark:text-blue-300'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              <FunnelIcon className="h-4 w-4" />
              Filtros
              {hasActiveFilters && (
                <span className="bg-blue-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {(filterAssignedTo ? 1 : 0) + (filterSector ? 1 : 0)}
                </span>
              )}
            </button>

            <button
              onClick={loadPipeline}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Atualizar
            </button>

            <button
              onClick={handleOpenNewModal}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm"
            >
              <PlusIcon className="h-4 w-4" />
              Nova Oportunidade
            </button>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-4 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Responsavel
                </label>
                <input
                  type="text"
                  value={filterAssignedTo}
                  onChange={(e) => setFilterAssignedTo(e.target.value)}
                  placeholder="Filtrar por responsavel..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Setor
                </label>
                <input
                  type="text"
                  value={filterSector}
                  onChange={(e) => setFilterSector(e.target.value)}
                  placeholder="Filtrar por setor..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                >
                  Limpar filtros
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Total de Etapas</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{stages.length}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Oportunidades</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{totalOpportunities}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 col-span-2">
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Valor Total do Pipeline</p>
            <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">{formatCurrency(totalPipelineValue)}</p>
          </div>
        </div>
      </div>

      {/* Kanban Board */}
      {stages.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <BuildingOffice2Icon className="h-16 w-16 text-gray-300 dark:text-gray-600 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Nenhuma etapa encontrada
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Configure as etapas do pipeline para comecar a gerenciar oportunidades.
          </p>
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: '500px' }}>
          {stages.map((stage) => (
            <KanbanColumn
              key={stage.id}
              stage={stage}
              allStages={stages}
              movingOpportunity={movingOpportunity}
              setMovingOpportunity={setMovingOpportunity}
              onMoveOpportunity={handleMoveOpportunity}
            />
          ))}
        </div>
      )}

      {/* New Opportunity Modal */}
      {showNewModal && (
        <NewOpportunityModal
          formData={formData}
          leads={leads}
          submitting={submitting}
          onFormChange={handleFormChange}
          onSubmit={handleSubmitOpportunity}
          onClose={handleCloseNewModal}
        />
      )}
    </div>
  )
}

/* ===================== Kanban Column ===================== */

const KanbanColumn = ({ stage, allStages, movingOpportunity, setMovingOpportunity, onMoveOpportunity }) => {
  const opportunities = stage.opportunities || []
  const stageColor = stage.color || '#6B7280'

  return (
    <div className="flex-shrink-0 w-80 flex flex-col bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
      {/* Column Header */}
      <div className="p-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <span
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: stageColor }}
            />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
              {stage.name}
            </h3>
          </div>
          <span className="bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs font-medium rounded-full h-5 min-w-[20px] px-1.5 flex items-center justify-center">
            {Number(stage.count) || opportunities.length}
          </span>
        </div>
      </div>

      {/* Cards */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2" style={{ maxHeight: '60vh' }}>
        {opportunities.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <p className="text-xs text-gray-400 dark:text-gray-500">
              Nenhuma oportunidade nesta etapa
            </p>
          </div>
        ) : (
          opportunities.map((opp) => (
            <OpportunityCard
              key={opp.id}
              opportunity={opp}
              stageColor={stageColor}
              allStages={allStages}
              currentStageId={stage.id}
              movingOpportunity={movingOpportunity}
              setMovingOpportunity={setMovingOpportunity}
              onMoveOpportunity={onMoveOpportunity}
            />
          ))
        )}
      </div>

      {/* Column Footer - Total Value */}
      <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-b-lg">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 dark:text-gray-400">Total</span>
          <span className="text-sm font-semibold text-gray-900 dark:text-white">
            {formatCurrency(stage.total_value)}
          </span>
        </div>
      </div>
    </div>
  )
}

/* ===================== Opportunity Card ===================== */

const OpportunityCard = ({
  opportunity,
  stageColor,
  allStages,
  currentStageId,
  movingOpportunity,
  setMovingOpportunity,
  onMoveOpportunity
}) => {
  const isMoving = movingOpportunity === opportunity.id

  return (
    <div
      className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow"
      style={{ borderLeft: `4px solid ${stageColor}` }}
    >
      <div className="p-3">
        {/* Company Name */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <BuildingOffice2Icon className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 truncate">
              {opportunity.company_name || 'Sem empresa'}
            </span>
          </div>
          <button
            onClick={() => setMovingOpportunity(isMoving ? null : opportunity.id)}
            className="flex-shrink-0 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title="Mover para outra etapa"
          >
            <ArrowsRightLeftIcon className="h-4 w-4 text-gray-400" />
          </button>
        </div>

        {/* Title */}
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 line-clamp-2">
          {opportunity.title}
        </h4>

        {/* Value */}
        <div className="flex items-center gap-1.5 mb-2">
          <CurrencyDollarIcon className="h-4 w-4 text-green-500" />
          <span className="text-sm font-semibold text-green-600 dark:text-green-400">
            {formatCurrency(opportunity.monthly_value)}
            <span className="text-xs font-normal text-gray-400">/mes</span>
          </span>
        </div>

        {/* Meta info */}
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-1">
            <ClockIcon className="h-3.5 w-3.5" />
            <span>{Number(opportunity.days_in_stage) || 0} dias</span>
          </div>
          <div className="flex items-center gap-1">
            <UserGroupIcon className="h-3.5 w-3.5" />
            <span>{Number(opportunity.contacts_count) || 0} contatos</span>
          </div>
        </div>

        {/* Move Dropdown */}
        {isMoving && (
          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Mover para:
            </label>
            <select
              className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              defaultValue=""
              onChange={(e) => {
                if (e.target.value) {
                  onMoveOpportunity(opportunity.id, parseInt(e.target.value))
                }
              }}
            >
              <option value="" disabled>Selecione a etapa...</option>
              {allStages
                .filter((s) => s.id !== currentStageId)
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
            </select>
          </div>
        )}
      </div>
    </div>
  )
}

/* ===================== New Opportunity Modal ===================== */

const NewOpportunityModal = ({ formData, leads, submitting, onFormChange, onSubmit, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Nova Oportunidade
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={onSubmit} className="p-5 space-y-4">
          {/* Lead */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Lead Associado
            </label>
            <select
              name="lead_id"
              value={formData.lead_id}
              onChange={onFormChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Selecione um lead (opcional)</option>
              {leads.map((lead) => (
                <option key={lead.id} value={lead.id}>
                  {lead.company_name || lead.contact_name || `Lead #${lead.id}`}
                </option>
              ))}
            </select>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Titulo *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={onFormChange}
              required
              placeholder="Ex: Locacao de 10 veiculos para empresa X"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Monthly Value + Total Vehicles */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Valor Mensal (R$) *
              </label>
              <input
                type="number"
                name="monthly_value"
                value={formData.monthly_value}
                onChange={onFormChange}
                required
                min="0"
                step="0.01"
                placeholder="0,00"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Total de Veiculos
              </label>
              <input
                type="number"
                name="total_vehicles"
                value={formData.total_vehicles}
                onChange={onFormChange}
                min="0"
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Contract Duration + Probability */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Duracao do Contrato (meses)
              </label>
              <input
                type="number"
                name="contract_duration"
                value={formData.contract_duration}
                onChange={onFormChange}
                min="0"
                placeholder="12"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Probabilidade (%)
              </label>
              <input
                type="number"
                name="probability"
                value={formData.probability}
                onChange={onFormChange}
                min="0"
                max="100"
                placeholder="50"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Expected Close Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Previsao de Fechamento
            </label>
            <input
              type="date"
              name="expected_close_date"
              value={formData.expected_close_date}
              onChange={onFormChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Observacoes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={onFormChange}
              rows={3}
              placeholder="Informacoes adicionais sobre a oportunidade..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm flex items-center gap-2"
            >
              {submitting ? (
                <>
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <PlusIcon className="h-4 w-4" />
                  Criar Oportunidade
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default MarketingPipelinePage
